import { useEffect, useRef } from 'react';
import type { Logger } from '@zg-rentals/logger-browser';
import {
  LOCAL_STORAGE_KEY_PREFIX,
  localStorageSafeGetItem,
  localStorageSafeParseAttachments,
  localStorageSafeRemoveItem,
  localStorageSafeSetItem,
} from '../utils/localStorageUtils';

// Generic type for attachments - applications should define their own AttachmentUpload type
export type AttachmentsUploadedForAddMessage<T = unknown> = Array<T>;

// Stringify uploaded attachments for localStorage storage
const stringifyUploadedAttachments = <T>(uploadedAttachments: AttachmentsUploadedForAddMessage<T>) => {
  return JSON.stringify(uploadedAttachments, [
    'file',
    'path',
    'lastModified',
    'lastModifiedDate',
    'name',
    'size',
    'type',
    'webkitRelativePath',
    'generatedPreviewUrl',
    'messageToken',
    'progress',
    'retryCount',
    'status',
    'zDocId',
  ]);
};

interface UseMessageDraftsOptions<T> {
  conversationId: string;
  logger: Logger;
  messageInputText: string;
  setMessageInputText: (text: string) => void;
  uploadedAttachments: AttachmentsUploadedForAddMessage<T>;
  setUploadedAttachments: (attachments: AttachmentsUploadedForAddMessage<T>) => void;
  onDraftSaved?: () => void;
}

/**
 * useMessageDrafts hook to manage message drafts in localStorage
 * Drafts are saved when a text or attachments exist in the textarea and
 * a user changes conversations
 */
export default <T = unknown>({
  conversationId,
  logger,
  messageInputText,
  setMessageInputText,
  uploadedAttachments,
  setUploadedAttachments,
  onDraftSaved,
}: UseMessageDraftsOptions<T>) => {
  const conversationIdRef = useRef(conversationId);
  const messageInputTextRef = useRef(messageInputText);
  const uploadedAttachmentsRef = useRef(uploadedAttachments);

  // Update refs when state changes
  useEffect(() => {
    messageInputTextRef.current = messageInputText;
  }, [messageInputText]);

  useEffect(() => {
    uploadedAttachmentsRef.current = uploadedAttachments;
  }, [uploadedAttachments]);

  // Update conversationId ref when it changes
  useEffect(() => {
    conversationIdRef.current = conversationId;
  }, [conversationId]);

  // Load drafts when conversationId changes and save on unmount
  useEffect(() => {
    // Load draft text for the new conversation
    const draftText = localStorageSafeGetItem(`${LOCAL_STORAGE_KEY_PREFIX.MESSAGE_DRAFT}${conversationId}`, '', logger);
    setMessageInputText(draftText || '');

    // Load draft attachments for the new conversation
    const uploads = localStorageSafeParseAttachments(
      `${LOCAL_STORAGE_KEY_PREFIX.ATTACHMENTS_DRAFT}${conversationId}`,
      [],
      logger,
    );

    setUploadedAttachments(uploads);

    // Update refs immediately with the loaded values
    messageInputTextRef.current = draftText || '';
    uploadedAttachmentsRef.current = uploads;

    // Save drafts on unmount (when changing conversations)
    // Refs which always have the latest values before the conversationId changes
    return () => {
      const currentConversationId = conversationIdRef.current;
      const hasText = Boolean(messageInputTextRef.current);
      const hasAttachments = Boolean(uploadedAttachmentsRef.current.length);
      const hasDraft = hasText || hasAttachments;

      if (hasText) {
        localStorageSafeSetItem(
          `${LOCAL_STORAGE_KEY_PREFIX.MESSAGE_DRAFT}${currentConversationId}`,
          messageInputTextRef.current,
          logger,
        );
      } else {
        localStorageSafeRemoveItem(`${LOCAL_STORAGE_KEY_PREFIX.MESSAGE_DRAFT}${currentConversationId}`, logger);
      }

      if (hasAttachments) {
        const stringifiedUploads = stringifyUploadedAttachments(uploadedAttachmentsRef.current);
        localStorageSafeSetItem(
          `${LOCAL_STORAGE_KEY_PREFIX.ATTACHMENTS_DRAFT}${currentConversationId}`,
          stringifiedUploads,
          logger,
        );
      } else {
        localStorageSafeRemoveItem(`${LOCAL_STORAGE_KEY_PREFIX.ATTACHMENTS_DRAFT}${currentConversationId}`, logger);
      }

      // Call the callback if drafts were saved
      if (hasDraft && onDraftSaved) {
        onDraftSaved();
      }
    };
  }, [conversationId, logger, setMessageInputText, setUploadedAttachments, onDraftSaved]);

  // Function to clear drafts (useful for when messages are sent)
  const clearMessageDrafts = () => {
    localStorageSafeRemoveItem(`${LOCAL_STORAGE_KEY_PREFIX.MESSAGE_DRAFT}${conversationId}`, logger);
    localStorageSafeRemoveItem(`${LOCAL_STORAGE_KEY_PREFIX.ATTACHMENTS_DRAFT}${conversationId}`, logger);
    setMessageInputText('');
    setUploadedAttachments([]);
  };

  const getHasLocalStorageMessageDraft = () => {
    const draftText = localStorageSafeGetItem(`${LOCAL_STORAGE_KEY_PREFIX.MESSAGE_DRAFT}${conversationId}`, '', logger);

    const uploads = localStorageSafeParseAttachments(
      `${LOCAL_STORAGE_KEY_PREFIX.ATTACHMENTS_DRAFT}${conversationId}`,
      [],
      logger,
    );

    return Boolean(draftText || uploads.length);
  };

  return {
    clearMessageDrafts,
    getHasLocalStorageMessageDraft,
  };
};
